<script lang="ts">
    import Am5Loader from '@/Util/Am5Loader.svelte';
    import type * as AM5XY from '@amcharts/amcharts5/xy';
    import { onDestroy } from 'svelte';
    import { getXertAm5, type am5Root, type am5xyChart } from '@/Util/XertAm5';
    import HistoricalMmpPopoverV2 from './HistoricalMmpPopoverV2.svelte';
    import type { HistoricalMmpDataResponse } from '@/Util/api';
    import { Activity } from '@/Common/ActivityFunctions';
    import Switch from '$lib/components/ui/switch/switch.svelte';
    import * as Popover from '$lib/components/ui/popover/index.js';
    import { gradePctToSinGrade, getKgeForGrade, scaleToRelativePower, getRelativePowerFactor } from '@/Util/relativePowerScale';
    import { debouncer } from '@/Util/utils';
    import route from '@/Util/routes';

    export let data: Array<{x: number, dur: string, userp?: number, calcp?: number, startp?: number, forecastp?: number}> = [];
    export let enableHistorical: boolean = true;
    export let fullLegend: boolean = true; // false hides Starting/Forecasted/Calculated from legend
    export let initiallyHidden: boolean = false; // hide chart until calculatedData is set (for guests)
    export let weight: number = 75; // user body weight in kg (internal, always kg)
    export let isMetric: boolean = true;
    export let calculatedData: Array<{x: number, dur: string, calcp: number}> | null = null;

    const LB_PER_KG = 2.20462262185;
    let weightDisplay: number = isMetric ? Math.round(weight * 10) / 10 : Math.round(weight * LB_PER_KG * 10) / 10;
    function onWeightInput() { weight = isMetric ? weightDisplay : weightDisplay / LB_PER_KG; }
    export const setCalculatedData = (pts: Array<{x: number, dur: string, calcp: number}> | null) => { calculatedData = pts; };

    const LABEL_TICKS = [1, 15, 60, 300, 600, 1200, 1800, 2700, 3600, 7200, 10800, 14400, 18000, 21600];

    let chartElement: HTMLElement;
    let root: am5Root;
    let chart: am5xyChart;
    let empiricalSeries: AM5XY.LineSeries;
    let startingSeries: AM5XY.LineSeries;
    let forecastedSeries: AM5XY.LineSeries;
    let historicalSeries: AM5XY.LineSeries;
    let calculatedSeries: AM5XY.LineSeries;
    let xAxis: AM5XY.ValueAxis<AM5XY.AxisRendererX>;
    let yAxis: AM5XY.ValueAxis<AM5XY.AxisRendererY>;
    let frozen = false;

    let historicalData: HistoricalMmpDataResponse | null = null;

    let showRelativePower = false;
    let grade = 0; // grade in percent, -3..+15

    $: kge = getKgeForGrade(gradePctToSinGrade(grade));

    const gradeDebounce = debouncer(180);

    let _firstLoad = true;
    export const reloadCurrentPowerCurve = () => {
        if (_firstLoad) { _firstLoad = false; return null; }
        return window.fetch(route('UserController@getCurrentPowerCurve'))
            .then(res => res.json())
            .then(d => { data = d.data; });
    };

    function snapToGrid(secs: number): number {
        if (secs <= 60)    return Math.round(secs);
        if (secs <= 120)   return Math.round(secs / 5)    * 5;
        if (secs <= 300)   return Math.round(secs / 10)   * 10;
        if (secs <= 600)   return Math.round(secs / 30)   * 30;
        if (secs <= 3600)  return Math.round(secs / 60)   * 60;   // 1 min, up to 1h
        if (secs <= 21600) return Math.round(secs / 300)  * 300;  // 5 min, up to 6h
        if (secs <= 43200) return Math.round(secs / 600)  * 600;  // 10 min, up to 12h
        if (secs <= 86400) return Math.round(secs / 1800) * 1800; // 30 min, up to 24h
        return               Math.round(secs / 3600) * 3600;      // 1 hour, beyond 24h
    }

    function formatDuration(secs: number): string {
        if (secs < 60) return `${secs}s`;
        if (secs < 3600) {
            const m = Math.floor(secs / 60);
            const s = secs % 60;
            return s === 0 ? `${m}m` : `${m}:${String(s).padStart(2, '0')}`;
        }
        const h = Math.floor(secs / 3600);
        const rem = secs % 3600;
        const m = Math.round(rem / 60);
        if (m === 60) return `${h + 1}h`;
        return rem === 0 ? `${h}h` : `${h}h${m}m`;
    }

    const SCALE_SQRT = 50;
    const SCALE_LOG  = 950;
    const xScale = (secs: number) => SCALE_SQRT * Math.sqrt(secs) + SCALE_LOG * Math.log(secs);
    const xScaleInv = (v: number) => {
        let y = v / SCALE_LOG;
        for (let i = 0; i < 24; i++) {
            const err = SCALE_SQRT * Math.exp(y / 2) + SCALE_LOG * y - v;
            if (Math.abs(err) < 1e-10) break;
            y -= err / (0.5 * SCALE_SQRT * Math.exp(y / 2) + SCALE_LOG);
        }
        return Math.exp(y);
    };

    const SIGNATURE_COLOR  = '#2563EB';
    const STARTING_COLOR   = '#8B5CF6';
    const FORECASTED_COLOR = '#F97316';
    const HISTORICAL_COLOR = '#92400E';
    const CALCULATED_COLOR = '#22C55E';

    function sigTooltipHtml(): string {
        return `<div style="display:flex;align-items:stretch;gap:9px;"><div style="width:3px;border-radius:2px;background:${SIGNATURE_COLOR};flex-shrink:0;"></div><div><div style="color:#93C5FD;font-size:9px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;margin-bottom:3px;">Fitness Signature</div><div style="color:#F1F5F9;font-size:14px;font-weight:700;line-height:1;">{valueY.formatNumber("#.")} <span style="font-size:11px;font-weight:500;color:#94A3B8;">W</span></div></div></div>`;
    }

    function startingTooltipHtml(): string {
        return `<div style="display:flex;align-items:stretch;gap:9px;"><div style="width:3px;border-radius:2px;background:${STARTING_COLOR};flex-shrink:0;"></div><div><div style="color:#C4B5FD;font-size:9px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;margin-bottom:3px;">Starting Fitness</div><div style="color:#F1F5F9;font-size:14px;font-weight:700;line-height:1;">{valueY.formatNumber("#.")} <span style="font-size:11px;font-weight:500;color:#94A3B8;">W</span></div></div></div>`;
    }

    function forecastedTooltipHtml(): string {
        return `<div style="display:flex;align-items:stretch;gap:9px;"><div style="width:3px;border-radius:2px;background:${FORECASTED_COLOR};flex-shrink:0;"></div><div><div style="color:#FDBA74;font-size:9px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;margin-bottom:3px;">Forecasted Fitness</div><div style="color:#F1F5F9;font-size:14px;font-weight:700;line-height:1;">{valueY.formatNumber("#.")} <span style="font-size:11px;font-weight:500;color:#94A3B8;">W</span></div></div></div>`;
    }

    function histTooltipHtml(): string {
        return `<div style="display:flex;align-items:stretch;gap:9px;"><div style="width:3px;border-radius:2px;background:${HISTORICAL_COLOR};flex-shrink:0;"></div><div><div style="color:#FCD34D;font-size:9px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;margin-bottom:3px;">Historical Best</div><div style="color:#F1F5F9;font-size:14px;font-weight:700;line-height:1;">{valueY.formatNumber("#.")} <span style="font-size:11px;font-weight:500;color:#94A3B8;">W</span></div><a href="{activityLink}" target="_blank" style="display:block;text-decoration:none;margin-top:3px;"><div style="color:#CBD5E1;font-size:10px;max-width:180px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">{activityName}</div><div style="color:#94A3B8;font-size:10px;margin-top:1px;">{activityDate} ↗</div></a></div></div>`;
    }

    function calcTooltipHtml(): string {
        return `<div style="display:flex;align-items:stretch;gap:9px;"><div style="width:3px;border-radius:2px;background:${CALCULATED_COLOR};flex-shrink:0;"></div><div><div style="color:#86EFAC;font-size:9px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;margin-bottom:3px;">Calculated Curve</div><div style="color:#F1F5F9;font-size:14px;font-weight:700;line-height:1;">{valueY.formatNumber("#.")} <span style="font-size:11px;font-weight:500;color:#94A3B8;">W</span></div></div></div>`;
    }

    function buildChart() {
        const { am5, am5xy, MyTheme } = getXertAm5();
        am5.addLicense('AM5C87942260');

        root = am5.Root.new(chartElement);
        root.setThemes([MyTheme.new(root)]);

        chart = root.container.children.push(am5xy.XYChart.new(root, {
            layout: root.verticalLayout,
            paddingTop: 8,
            paddingRight: 20,
            paddingBottom: 0,
            paddingLeft: 0,
        }));

        const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 40 });
        const xAxisTooltip = am5.Tooltip.new(root, {
            themeTags: ['axis'],
            getFillFromSprite: false,
            autoTextColor: false,
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (xAxisTooltip.get('background') as any).setAll({
            fill: am5.color('#1E293B'),
            fillOpacity: 0.97,
            stroke: am5.color('#334155'),
            strokeOpacity: 1,
            strokeWidth: 1,
            cornerRadiusBL: 6,
            cornerRadiusBR: 6,
            cornerRadiusTL: 6,
            cornerRadiusTR: 6,
            shadowColor: am5.color('#000000'),
            shadowBlur: 10,
            shadowOffsetY: 3,
            shadowOpacity: 0.35,
        });
        xAxisTooltip.label.setAll({
            fill: am5.color('#CBD5E1'),
            fontSize: 11,
            fontWeight: '600',
            paddingTop: 5,
            paddingBottom: 5,
            paddingLeft: 10,
            paddingRight: 10,
        });

        xAxis = chart.xAxes.push(am5xy.ValueAxis.new(root, {
            renderer: xRenderer,
            min: 0,
            extraMin: 0,
            extraMax: 0.05,
            tooltip: xAxisTooltip,
        }));

        xAxisTooltip.label.adapters.add('text', (_text) => {
            const raw = parseFloat((_text ?? '').replace(/[^\d.eE+-]/g, ''));
            return !isNaN(raw) && raw >= 0 ? formatDuration(snapToGrid(xScaleInv(raw))) : (_text ?? '');
        });

        xRenderer.labels.template.set('forceHidden', true);
        xRenderer.grid.template.set('forceHidden', true);

        for (const pos of LABEL_TICKS) {
            const dataItem = xAxis.makeDataItem({ value: xScale(pos) });
            xAxis.createAxisRange(dataItem);
            dataItem.get('grid')?.setAll({ strokeOpacity: 0.18, stroke: am5.color('#6B7280'), visible: true });
            dataItem.get('label')?.setAll({
                text: formatDuration(pos),
                visible: true,
                forceHidden: false,
                fontSize: 11,
                fill: am5.color('#6B7280'),
            });
            dataItem.get('tick')?.setAll({ visible: true, length: 4, strokeOpacity: 0.4 });
        }

        const cursor = chart.set('cursor', am5xy.XYCursor.new(root, { behavior: 'none', xAxis }));
        cursor.lineY.set('visible', false);
        cursor.lineX.setAll({ strokeDasharray: [4, 3], strokeOpacity: 0.35, stroke: am5.color('#6B7280') });

        const yRenderer = am5xy.AxisRendererY.new(root, {});
        yRenderer.grid.template.setAll({ strokeOpacity: 0.08, strokeDasharray: [3, 4] });
        yRenderer.labels.template.setAll({ fontSize: 11, fill: am5.color('#6B7280') });

        yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
            renderer: yRenderer,
            min: 0,
            strictMinMax: false,
        }));

        const makeTooltip = (accentColor: string, html: string) => {
            const tip = am5.Tooltip.new(root, {
                labelHTML: html,
                getFillFromSprite: false,
                autoTextColor: false,
                pointerOrientation: 'left',
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (tip.get('background') as any).setAll({
                fill: am5.color('rgba(15, 23, 42, 0.4)'),
                stroke: am5.color(accentColor),
                strokeOpacity: 0.55,
                strokeWidth: 1,
                cornerRadiusBL: 5,
                cornerRadiusBR: 5,
                cornerRadiusTL: 5,
                cornerRadiusTR: 5,
                paddingTop: 2,
                paddingBottom: 2,
                paddingLeft: 3,
                paddingRight: 4,
            });
            tip.label.setAll({ interactive: true });
            return tip;
        };

        empiricalSeries = chart.series.push(am5xy.LineSeries.new(root, {
            xAxis,
            yAxis,
            valueXField: 'duration',
            valueYField: 'powerCurve',
            name: 'Current Power Curve',
            fill: am5.color(SIGNATURE_COLOR),
            stroke: am5.color(SIGNATURE_COLOR),
            tooltip: makeTooltip(SIGNATURE_COLOR, sigTooltipHtml()),
        }));
        empiricalSeries.fills.template.setAll({
            visible: true,
            fillGradient: am5.LinearGradient.new(root, {
                stops: [
                    { color: am5.color(SIGNATURE_COLOR), opacity: 0.28 },
                    { color: am5.color(SIGNATURE_COLOR), opacity: 0.03 },
                ],
                rotation: 90,
            }),
        });
        empiricalSeries.strokes.template.setAll({ strokeWidth: 1.5, strokeOpacity: 0.65 });

        startingSeries = chart.series.push(am5xy.LineSeries.new(root, {
            xAxis,
            yAxis,
            valueXField: 'duration',
            valueYField: 'startingPower',
            name: 'Starting Fitness',
            stroke: am5.color(STARTING_COLOR),
            tooltip: makeTooltip(STARTING_COLOR, startingTooltipHtml()),
        }));
        startingSeries.strokes.template.setAll({ strokeWidth: 1.5, strokeDasharray: [5, 4] });
        startingSeries.hide();

        forecastedSeries = chart.series.push(am5xy.LineSeries.new(root, {
            xAxis,
            yAxis,
            valueXField: 'duration',
            valueYField: 'forecastedPower',
            name: 'Forecasted Fitness',
            stroke: am5.color(FORECASTED_COLOR),
            tooltip: makeTooltip(FORECASTED_COLOR, forecastedTooltipHtml()),
        }));
        forecastedSeries.strokes.template.setAll({ strokeWidth: 1.5, strokeDasharray: [5, 4] });
        forecastedSeries.hide();

        historicalSeries = chart.series.push(am5xy.LineSeries.new(root, {
            xAxis,
            yAxis,
            valueXField: 'duration',
            valueYField: 'historicalMmp',
            name: 'Historical Best',
            stroke: am5.color(HISTORICAL_COLOR),
            tooltip: makeTooltip(HISTORICAL_COLOR, histTooltipHtml()),
        }));
        // Solid line (vs. the dashed Starting/Forecasted strokes) so it reads
        // clearly against the chart; the activity-change dots below are what
        // distinguish it as a trace of discrete measured efforts.
        historicalSeries.strokes.template.setAll({ strokeWidth: 1.5, strokeOpacity: 0.6 });
        // Mark each point where the underlying source activity changes, since the
        // historical curve is stitched together from many different activities'
        // best efforts rather than being one continuous ride.
        historicalSeries.bullets.push((bulletRoot, _series, dataItem) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const dc = dataItem.dataContext as any;
            if (!dc?.activityChanged) return undefined;
            return am5.Bullet.new(bulletRoot, {
                sprite: am5.Circle.new(bulletRoot, {
                    radius: 3,
                    fill: am5.color(HISTORICAL_COLOR),
                    stroke: am5.color(HISTORICAL_COLOR),
                    strokeOpacity: 0.4,
                }),
            });
        });
        historicalSeries.hide();

        calculatedSeries = chart.series.push(am5xy.LineSeries.new(root, {
            xAxis,
            yAxis,
            valueXField: 'duration',
            valueYField: 'calculatedPower',
            name: 'Calculated Curve',
            stroke: am5.color(CALCULATED_COLOR),
            tooltip: makeTooltip(CALCULATED_COLOR, calcTooltipHtml()),
        }));
        calculatedSeries.strokes.template.setAll({ strokeWidth: 2, strokeDasharray: [4, 4] });
        calculatedSeries.hide();

        cursor.set('snapToSeries', [empiricalSeries]);
        cursor.set('snapToSeriesBy', 'x');

        chart.plotContainer.events.on('click', () => {
            if (!frozen) {
                const posX = cursor.getPrivate('positionX');
                if (posX != null) {
                    frozen = true;
                    cursor.set('snapToSeries', undefined);
                    cursor.set('positionX', posX);
                    cursor.set('alwaysShow', true);
                    cursor.lineX.setAll({ stroke: am5.color('#60A5FA'), strokeOpacity: 0.7, strokeDasharray: [] });
                }
            } else {
                frozen = false;
                cursor.set('positionX', undefined);
                cursor.set('alwaysShow', false);
                cursor.lineX.setAll({ strokeDasharray: [4, 3], strokeOpacity: 0.35, stroke: am5.color('#6B7280') });
                cursor.set('snapToSeries', [empiricalSeries]);
            }
        });

        const legend = chart.children.push(am5.Legend.new(root, {
            centerX: am5.percent(50),
            x: am5.percent(50),
            marginTop: 12,
            marginBottom: 0,
        }));
        legend.data.setAll(fullLegend
            ? chart.series.values
            : [empiricalSeries, historicalSeries]);
        legend.labels.template.setAll({ fontSize: 12, fill: am5.color('#374151') });
        legend.markers.template.setAll({ width: 12, height: 12 });

        chart.appear(400, 100);

        // Populate chart with initial data
        updateChart();
    }

    onDestroy(() => {
        root?.dispose();
    });

    $: if (chartElement && !root) buildChart();
    $: if (root && data && showRelativePower !== undefined) updateChart();
    $: if (root && historicalData && showRelativePower !== undefined) applyHistoricalSeries();
    $: if (root && calculatedData !== undefined && showRelativePower !== undefined) applyCalculatedSeries();
    $: if (yAxis) {
        if (showRelativePower) {
            const oneSecPower = Math.max(
                data?.find(pt => pt.x === 1)?.userp ?? 0,
                calculatedData?.find(pt => pt.x === 1)?.calcp ?? 0,
            );
            const maxFactor = Math.max(
                getRelativePowerFactor(weight, gradePctToSinGrade(-3)),
                getRelativePowerFactor(weight, gradePctToSinGrade(15))
            );
            if (oneSecPower > 0) {
                yAxis.setAll({ max: oneSecPower * maxFactor, strictMinMax: true });
            } else {
                yAxis.setAll({ max: undefined, strictMinMax: false });
            }
        } else {
            yAxis.setAll({ max: undefined, strictMinMax: false });
        }
    }
    $: grade, weight, gradeDebounce.debounce(() => {
        if (root && data) updateChart();
        if (root && historicalData) applyHistoricalSeries();
        if (root) applyCalculatedSeries();
    });

    function updateChart() {
        if (!empiricalSeries || !data) return;
        const sg = gradePctToSinGrade(grade);
        const basePts = (data ?? []).filter(pt => pt.x > 0);

        empiricalSeries.data.setAll(basePts.map(pt => {
            const rawPower = pt.userp ?? pt.calcp ?? 0;
            return {
                duration: xScale(pt.x),
                powerCurve: showRelativePower ? scaleToRelativePower(rawPower, weight, sg) : rawPower,
                durationLabel: pt.dur,
            };
        }));

        const startPts = basePts.filter(pt => (pt.startp ?? 0) > 0);
        if (startPts.length > 0) {
            startingSeries.data.setAll(startPts.map(pt => ({
                duration: xScale(pt.x),
                startingPower: showRelativePower ? scaleToRelativePower(pt.startp!, weight, sg) : pt.startp!,
                durationLabel: pt.dur,
            })));
            startingSeries.show();
        } else {
            startingSeries.data.setAll([]);
            startingSeries.hide();
        }

        const forecastPts = basePts.filter(pt => (pt.forecastp ?? 0) > 0);
        if (forecastPts.length > 0) {
            forecastedSeries.data.setAll(forecastPts.map(pt => ({
                duration: xScale(pt.x),
                forecastedPower: showRelativePower ? scaleToRelativePower(pt.forecastp!, weight, sg) : pt.forecastp!,
                durationLabel: pt.dur,
            })));
            forecastedSeries.show();
        } else {
            forecastedSeries.data.setAll([]);
            forecastedSeries.hide();
        }
    }

    function applyHistoricalSeries() {
        if (!historicalData || !historicalSeries) return;
        const sg = gradePctToSinGrade(grade);
        let prevActivityPath: string | null = null;
        const pts = historicalData.durations
            .map((d, i) => {
                const rawPower = historicalData!.powers[i] > 0 ? historicalData!.powers[i] : undefined;
                if (rawPower === undefined) return null;
                const activityPath = historicalData!.attributions[i]?.path ?? '';
                const activityChanged = prevActivityPath !== null && activityPath !== prevActivityPath;
                prevActivityPath = activityPath;
                return {
                    duration: xScale(d),
                    durationLabel: formatDuration(d),
                    historicalMmp: showRelativePower ? scaleToRelativePower(rawPower, weight, sg) : rawPower,
                    activityName: historicalData!.attributions[i]?.name ?? '',
                    activityDate: historicalData!.attributions[i]?.date ?? '',
                    activityLink: Activity.getDetailsPageLink({ activity_mode: 'activity', path: activityPath } as never) ?? '',
                    activityChanged,
                };
            })
            .filter((pt): pt is NonNullable<typeof pt> => pt !== null);
        historicalSeries.data.setAll(pts);
        historicalSeries.show();
    }

    function onHistoricalLoad(d: HistoricalMmpDataResponse) {
        historicalData = d;
        applyHistoricalSeries();
    }

    function onHistoricalClear() {
        historicalData = null;
        historicalSeries?.data.setAll([]);
        historicalSeries?.hide();
    }

    function applyCalculatedSeries() {
        if (!calculatedSeries) return;
        if (!calculatedData) {
            calculatedSeries.data.setAll([]);
            calculatedSeries.hide();
            return;
        }
        const sg = gradePctToSinGrade(grade);
        const pts = calculatedData
            .filter(pt => pt.x > 0)
            .map(pt => ({
                duration: xScale(pt.x),
                calculatedPower: showRelativePower
                    ? scaleToRelativePower(pt.calcp, weight, sg)
                    : pt.calcp,
                durationLabel: pt.dur,
            }));
        calculatedSeries.data.setAll(pts);
        calculatedSeries.show();
    }
</script>

<div class="space-y-2">
    {#if enableHistorical}
        <HistoricalMmpPopoverV2
            hasData={!!historicalData}
            onLoad={onHistoricalLoad}
            onClear={onHistoricalClear}
            maxDuration={data?.at(-1)?.x ?? null}
        />
    {/if}

    <div class="flex items-center rounded-lg border border-border bg-muted/30 p-1 self-start">
        <!-- svelte-ignore a11y-label-has-associated-control -->
        <label class="flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1 transition-colors hover:bg-muted/60 mb-0">
            <Switch bind:checked={showRelativePower} />
            <span class="text-xs font-medium {showRelativePower ? 'text-foreground' : 'text-muted-foreground'}">Relative Power</span>
        </label>
        {#if showRelativePower}
            <div class="h-4 w-px bg-border" />
            <div class="flex items-center gap-3 px-3 py-0.5">
                <div class="flex items-center gap-1.5">
                    <span class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Weight</span>
                    <input
                        type="number"
                        min={isMetric ? 20 : 44}
                        max={isMetric ? 200 : 441}
                        step={isMetric ? 0.5 : 1}
                        bind:value={weightDisplay}
                        on:input={onWeightInput}
                        class="w-14 rounded-md border border-input bg-background px-2 py-1 text-xs tabular-nums text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                    <span class="text-[11px] text-muted-foreground">{isMetric ? 'kg' : 'lbs'}</span>
                    {#if !isMetric}
                        <span class="text-[11px] text-muted-foreground/60">({(weight).toFixed(1)}&nbsp;kg)</span>
                    {/if}
                </div>
                <div class="h-4 w-px bg-border" />
                <div class="flex items-center gap-2">
                    <span class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Grade</span>
                    <input
                        type="range"
                        min={-3}
                        max={15}
                        step={0.5}
                        bind:value={grade}
                        class="w-24 cursor-pointer accent-primary"
                    />
                    <span class="min-w-[2.25rem] text-right text-xs tabular-nums font-medium text-foreground">
                        {grade > 0 ? '+' : ''}{grade}%
                    </span>
                </div>
                <div class="h-4 w-px bg-border" />
                <div class="flex items-center gap-1.5">
                    <span class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">KGE</span>
                    <span class="min-w-[2.5rem] text-right text-xs tabular-nums font-medium text-foreground">
                        {kge.toFixed(3)}
                    </span>
                </div>
                <Popover.Root portal="#shadcn-ui-dialog-target">
                    <Popover.Trigger asChild let:builder>
                        <button
                            use:builder.action {...builder}
                            type="button"
                            class="flex h-4 w-4 items-center justify-center rounded-full border border-border text-[10px] font-semibold text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                            aria-label="How relative power is calculated"
                        >
                            ?
                        </button>
                    </Popover.Trigger>
                    <Popover.Content class="w-80 text-xs leading-relaxed" align="end">
                        <p class="mb-2 font-semibold text-foreground">How relative power is calculated</p>
                        <p class="mb-2 text-muted-foreground">
                            When <span class="font-medium text-foreground">Relative Power</span> is on, the power curve is
                            re-expressed as the equivalent power a <span class="font-medium text-foreground">75&nbsp;kg
                            reference rider on a 9&nbsp;kg bike</span> would need to ride at the same speed.
                        </p>
                        <p class="mb-2 text-muted-foreground">
                            Each point is scaled by the mass ratio of the reference rider to your
                            <span class="font-medium text-foreground">weight</span> (rider&nbsp;+&nbsp;bike), raised to the
                            <span class="font-medium text-foreground">KGE</span> exponent:
                        </p>
                        <p class="mb-2 rounded bg-muted/50 px-2 py-1 text-center font-mono text-foreground">
                            P<sub>rel</sub> = P × ((75 + 9) / (w + 9))<sup>KGE</sup>
                        </p>
                        <p class="text-muted-foreground">
                            The <span class="font-medium text-foreground">grade</span> sets how steep the climb is and
                            converts into the KGE exponent in the denominator: on the flat, aerodynamic drag dominates and
                            KGE is small; as the grade increases, gravity dominates and KGE approaches&nbsp;1, so heavier
                            riders are penalized more.
                        </p>
                    </Popover.Content>
                </Popover.Root>
            </div>
        {/if}
    </div>

    {#if !initiallyHidden || calculatedData !== null}
    <Am5Loader>
        <div bind:this={chartElement} class="w-full h-[500px]"></div>
    </Am5Loader>
    {/if}
</div>
